import {
  type CornerPosition,
  normalizePlacement,
} from '@circuit/chat/widget-chrome/tokens';
/* eslint-disable no-param-reassign -- this whole module exists to mutate the StateRefs passed in; per-line disables would noise up every helper */
import {
  closeNudgeSignY,
  IFRAME_WIDTH_PX,
} from '@circuit/chat/widget-chrome/tokens';

/**
 * Per-instance launcher state machine. Owns the iframe + message listener
 * lifecycle and exposes the operations `mountLauncher` wires into the bubble
 * + global API.
 */
import {
  type ChatStyle,
  type ConfigMessage,
  type ConfigPayload,
  type EmbedMessage,
  type EmptyPayload,
  type LauncherToSpaType,
  isBadgeCountMessage,
  isCloseRequestMessage,
  isFullscreenRequestMessage,
  isHeightChangeMessage,
  isSetSizeMessage,
  isWidgetReadyMessage,
  normalizeChatStyle,
  PROTOCOL_VERSION,
} from '@/shared/protocol';

import { launcherLogger } from './beaconLog';
import {
  EMBED_ORIGIN,
  isAllowedOrigin,
  WIDGET_READY_TIMEOUT_MS,
  WIDGET_READY_WARN_MS,
} from './constants';
import {
  type SizeState,
  applyFullscreen,
  applyIframeSize,
  centerOffset,
  centerTransform,
  createIframe,
  IFRAME_HEIGHT_PX,
  renderBadge,
  setBubbleOpen,
  setIndicatorOpen,
} from './dom';

export interface MountArgs {
  apiKey: string;
  config: ConfigPayload;
  /** Open the widget immediately on mount (`?open=true` on the script src). */
  defaultOpen?: boolean;
  scriptSrc: string;
}

export interface LauncherState {
  readonly badge: HTMLSpanElement | null;
  readonly bubble: HTMLButtonElement | null;
  readonly indicator: HTMLSpanElement | null;
  handleInbound: (event: MessageEvent) => void;
  hideIframe: () => void;
  /** True while the widget is user-visible (reveal in flight or complete). */
  isWidgetOpen: () => boolean;
  openWidget: () => void;
  postToSpa: (type: LauncherToSpaType) => void;
  prewarmIframe: () => void;
  setBadge: (el: HTMLSpanElement) => void;
  setBubble: (el: HTMLButtonElement) => void;
  setIndicator: (el: HTMLSpanElement) => void;
  teardown: () => void;
}

/**
 * All mutable state for one launcher instance, packed into an object so
 * module-scope helpers (which can't see closure variables) can mutate it.
 */
interface StateRefs {
  apiKey: string;
  badge: HTMLSpanElement | null;
  bubble: HTMLButtonElement | null;
  chatStyle: ChatStyle;
  config: ConfigPayload;
  cornerPosition: CornerPosition;
  hideTimeoutId: ReturnType<typeof setTimeout> | null;
  iframe: HTMLIFrameElement | null;
  indicator: HTMLSpanElement | null;
  isFullscreen: boolean;
  lastSize: SizeState;
  messageListener: ((event: MessageEvent) => void) | null;
  /** True when openByDefault=true: no launcher bubble is rendered so the
   * iframe geometry skips reserving bubble size + clearance.
   */
  omitLauncherChrome: boolean;
  pendingOutbound: LauncherToSpaType[];
  prewarmStartedAt: number;
  readyTimeoutId: ReturnType<typeof setTimeout> | null;
  readyWarnTimeoutId: ReturnType<typeof setTimeout> | null;
  scriptSrc: string;
  widgetOpen: boolean;
  widgetReady: boolean;
}

function buildConfigMessage(config: ConfigPayload): ConfigMessage {
  return {
    payload: { ...config, hostOrigin: globalThis.location.origin },
    type: 'CONFIG',
    version: PROTOCOL_VERSION,
  };
}

function buildEmptyMessage(
  type: LauncherToSpaType,
): EmbedMessage<LauncherToSpaType, EmptyPayload> {
  const payload = {};
  return { payload, type, version: PROTOCOL_VERSION };
}

function resolveEmbedUrl(
  scriptSrc: string,
  apiKey: string,
  style: ChatStyle,
): string {
  const url = new URL(scriptSrc, globalThis.location.href);
  url.pathname = '/embed/';
  url.search = '';
  url.hash = '';
  url.searchParams.set('apiKey', apiKey);
  // Style hint picked up by the pre-React skeleton (embed/index.html).
  url.searchParams.set('style', style);
  return url.toString();
}

function clearReadyTimeout(state: StateRefs): void {
  if (state.readyTimeoutId !== null) {
    clearTimeout(state.readyTimeoutId);
    state.readyTimeoutId = null;
  }
}
function clearReadyWarnTimeout(state: StateRefs): void {
  if (state.readyWarnTimeoutId !== null) {
    clearTimeout(state.readyWarnTimeoutId);
    state.readyWarnTimeoutId = null;
  }
}
function clearHideTimeout(state: StateRefs): void {
  if (state.hideTimeoutId !== null) {
    clearTimeout(state.hideTimeoutId);
    state.hideTimeoutId = null;
  }
}

function teardownMessageListener(state: StateRefs): void {
  if (state.messageListener) {
    globalThis.removeEventListener('message', state.messageListener);
    state.messageListener = null;
  }
}

function teardownIframe(state: StateRefs): void {
  clearReadyTimeout(state);
  clearReadyWarnTimeout(state);
  clearHideTimeout(state);
  teardownMessageListener(state);
  state.iframe?.remove();
  state.iframe = null;
  state.widgetReady = false;
  state.pendingOutbound = [];
}

function postToSpa(state: StateRefs, type: LauncherToSpaType): void {
  if (!state.iframe) return;
  // Queue outbound messages that arrive before the SPA signals
  // WIDGET_READY. Without this a host that calls `window.CircuitChat.open()`
  // on a cold load would drop the WIDGET_OPEN before the SPA is listening.
  if (!state.widgetReady) {
    // Collapse the queue to the last message of each type. Rapid
    // open/close/open before ready would otherwise flash the SPA through
    // the whole sequence on flush; the intent is 'apply the latest state'.
    state.pendingOutbound = state.pendingOutbound.filter(
      (pending) => pending !== type,
    );
    state.pendingOutbound.push(type);
    return;
  }
  state.iframe.contentWindow?.postMessage(
    buildEmptyMessage(type),
    EMBED_ORIGIN,
  );
}

function flushPendingOutbound(state: StateRefs): void {
  if (!state.iframe || !state.widgetReady) return;
  const target = state.iframe.contentWindow;
  if (!target) return;
  for (const type of state.pendingOutbound) {
    target.postMessage(buildEmptyMessage(type), EMBED_ORIGIN);
  }
  state.pendingOutbound = [];
}

/** Duration the close-fade runs before we hard-hide the iframe. */
const HIDE_FADE_MS = 280;

function revealIframe(state: StateRefs): void {
  if (!state.iframe) return;
  // Clear the HARD teardown timer — a revealed iframe belongs to the user
  // and must not be yanked. The soft warn timer stays armed so integrators
  // debugging a stuck SPA see a console warning if WIDGET_READY never fires.
  clearReadyTimeout(state);
  clearHideTimeout(state);
  if (!state.widgetReady && state.readyWarnTimeoutId === null) {
    state.readyWarnTimeoutId = setTimeout(() => {
      state.readyWarnTimeoutId = null;
      if (!state.widgetReady) {
        // eslint-disable-next-line no-console -- diagnostic for embed integrators
        console.warn(
          '[circuit-chat-embed] SPA has not signaled WIDGET_READY after ' +
            `${WIDGET_READY_WARN_MS}ms; the widget may appear stuck.`,
        );
      }
    }, WIDGET_READY_WARN_MS);
  }
  state.iframe.style.visibility = 'visible';
  state.widgetOpen = true;
  void state.iframe.offsetHeight;
  state.iframe.style.opacity = '1';
  state.iframe.style.pointerEvents = 'auto';
  state.iframe.style.transform = currentTransform(state);
  if (state.indicator) setIndicatorOpen(state.indicator, true);
  if (state.bubble) setBubbleOpen(state.bubble, true);
  if (state.badge) state.badge.style.visibility = 'hidden';
}

function hideIframe(state: StateRefs): void {
  if (!state.iframe) return;
  clearHideTimeout(state);
  state.iframe.style.opacity = '0';
  state.widgetOpen = false;
  state.iframe.style.pointerEvents = 'none';
  // Mirror revealIframe's transform on the X axis so center-placed panels
  // don't jump sideways on close; just add a small Y offset for the fade.
  state.iframe.style.transform = closeTransform(state);
  state.hideTimeoutId = setTimeout(() => {
    if (state.iframe?.style.opacity === '0') {
      state.iframe.style.visibility = 'hidden';
    }
    state.hideTimeoutId = null;
  }, HIDE_FADE_MS);
  if (state.indicator) setIndicatorOpen(state.indicator, false);
  if (state.bubble) setBubbleOpen(state.bubble, false);
  if (state.badge) state.badge.style.visibility = 'visible';
}

function currentTransform(state: StateRefs): string {
  return centerTransform({
    cornerPosition: state.cornerPosition,
    height: state.lastSize.height,
    omitLauncherChrome: state.omitLauncherChrome,
    placement: state.lastSize.placement,
    style: state.chatStyle,
    width: state.lastSize.width,
  });
}

/** Same as currentTransform, but nudges the iframe by 20px in the launcher's
 *  direction on close so the panel appears to fall back toward the launcher.
 *  For top-anchored positions that means -20px (upward); for middle-anchored
 *  positions the y offset is 0 so the fade stays symmetric. Horizontal
 *  offset from center placement is preserved so the panel doesn't jump
 *  sideways on close. */
function closeTransform(state: StateRefs): string {
  const { x, y } = centerOffset({
    cornerPosition: state.cornerPosition,
    height: state.lastSize.height,
    omitLauncherChrome: state.omitLauncherChrome,
    placement: state.lastSize.placement,
    style: state.chatStyle,
    width: state.lastSize.width,
  });
  const dy = closeNudgeSignY(state.cornerPosition) * 20;
  return `translate(${x}px, ${y + dy}px)`;
}

function handleFullscreen(
  state: StateRefs,
  target: HTMLIFrameElement,
  enabled: boolean,
): void {
  if (enabled) {
    if (state.isFullscreen) return;
    applyFullscreen(target);
    state.isFullscreen = true;
    return;
  }
  if (!state.isFullscreen) return;
  applyIframeSize(
    target,
    state.lastSize,
    state.cornerPosition,
    state.chatStyle,
    state.omitLauncherChrome,
  );
  state.isFullscreen = false;
}

function handleResizeMessage(
  state: StateRefs,
  target: HTMLIFrameElement,
  envelope: unknown,
): void {
  if (isSetSizeMessage(envelope)) {
    state.lastSize = applyIframeSize(
      target,
      envelope.payload,
      state.cornerPosition,
      state.chatStyle,
      state.omitLauncherChrome,
    );
    state.isFullscreen = false;
    return;
  }
  if (isHeightChangeMessage(envelope)) {
    state.lastSize = applyIframeSize(
      target,
      {
        height: envelope.payload.height,
        placement: state.lastSize.placement,
        width: state.lastSize.width,
      },
      state.cornerPosition,
      state.chatStyle,
      state.omitLauncherChrome,
    );
    state.isFullscreen = false;
    return;
  }
  if (isBadgeCountMessage(envelope) && state.badge) {
    renderBadge(state.badge, envelope.payload.count);
    return;
  }
  if (isFullscreenRequestMessage(envelope)) {
    handleFullscreen(state, target, envelope.payload.enabled);
  }
}

function handleInbound(state: StateRefs, event: MessageEvent): void {
  if (!state.iframe) return;
  const envelope: unknown = event.data;
  if (isWidgetReadyMessage(envelope)) {
    clearReadyTimeout(state);
    clearReadyWarnTimeout(state);
    state.widgetReady = true;
    launcherLogger.info({
      context: { readyLatencyMs: Date.now() - state.prewarmStartedAt },
      event: { type: 'chat_embed.launcher_widget_ready' },
      message: 'launcher: WIDGET_READY received',
    });
    state.iframe.contentWindow?.postMessage(
      buildConfigMessage(state.config),
      EMBED_ORIGIN,
    );
    flushPendingOutbound(state);
    return;
  }
  if (isCloseRequestMessage(envelope)) {
    launcherLogger.info({
      event: { type: 'chat_embed.launcher_close_request' },
      message: 'launcher: CLOSE_REQUEST received',
    });
    hideIframe(state);
    return;
  }
  handleResizeMessage(state, state.iframe, envelope);
}

function prewarmIframe(state: StateRefs): void {
  if (state.iframe) return;
  const embedUrl = state.scriptSrc
    ? resolveEmbedUrl(state.scriptSrc, state.apiKey, state.chatStyle)
    : `/embed/?apiKey=${encodeURIComponent(state.apiKey)}&style=${state.chatStyle}`;
  state.iframe = createIframe(
    embedUrl,
    state.cornerPosition,
    state.chatStyle,
    state.omitLauncherChrome,
  );
  // Seed the initial transform to the closeTransform offset so the reveal
  // transitions in place. Without this, windowed's first open slides from
  // the corner because createIframe's default nudge is still corner-anchored
  // via cornerStyles().
  state.iframe.style.transform = closeTransform(state);
  state.prewarmStartedAt = Date.now();
  // Capture the iframe in a closure. A late message from a torn-down
  // previous iframe would still fire this listener until
  // teardownMessageListener runs; comparing against the closure-captured
  // reference (not state.iframe, which points at the current iframe by then)
  // rejects that.
  const openedIframe = state.iframe;
  state.messageListener = (event: MessageEvent): void => {
    if (!isAllowedOrigin(event.origin)) return;
    if (event.source !== openedIframe.contentWindow) return;
    handleInbound(state, event);
  };
  globalThis.addEventListener('message', state.messageListener);
  state.readyTimeoutId = setTimeout(() => {
    // Surface stuck-widget events in Datadog BEFORE tearing the iframe down.
    // By definition the SPA never mounted, so its own window.error handlers
    // never got a chance to fire.
    launcherLogger.error({
      context: { timeoutMs: WIDGET_READY_TIMEOUT_MS },
      message: 'launcher: WIDGET_READY timeout',
      event: {
        outcome: 'failure',
        type: 'chat_embed.launcher_widget_ready_timeout',
      },
    });
    teardownIframe(state);
  }, WIDGET_READY_TIMEOUT_MS);
  document.body.appendChild(state.iframe);
}

export function createLauncherState({
  apiKey,
  config,
  scriptSrc,
}: MountArgs): LauncherState {
  const chatStyle = normalizeChatStyle(config.branding.style);
  const omitLauncherChrome = config.branding.openByDefault ?? false;
  const state: StateRefs = {
    apiKey,
    badge: null,
    bubble: null,
    chatStyle,
    config,
    cornerPosition: normalizePlacement(config.branding.position),
    hideTimeoutId: null,
    iframe: null,
    indicator: null,
    isFullscreen: false,
    messageListener: null,
    omitLauncherChrome,
    pendingOutbound: [],
    prewarmStartedAt: 0,
    readyTimeoutId: null,
    readyWarnTimeoutId: null,
    scriptSrc,
    widgetOpen: false,
    widgetReady: false,
    lastSize: {
      // Seed default size so an early HEIGHT_CHANGE (before the SPA's first
      // SET_SIZE) picks the right base dimensions. Windowed and floating
      // currently share the same base pane; see dom.ts if they diverge.
      height: IFRAME_HEIGHT_PX,
      placement: 'corner',
      width: IFRAME_WIDTH_PX,
    },
  };

  return {
    get badge() {
      return state.badge;
    },
    get bubble() {
      return state.bubble;
    },
    handleInbound: (event) => {
      handleInbound(state, event);
    },
    hideIframe: () => {
      hideIframe(state);
    },
    get indicator() {
      return state.indicator;
    },
    isWidgetOpen: () => state.widgetOpen,
    openWidget: () => {
      prewarmIframe(state);
      revealIframe(state);
    },
    postToSpa: (type) => {
      postToSpa(state, type);
    },
    prewarmIframe: () => {
      prewarmIframe(state);
    },
    setBadge: (el) => {
      state.badge = el;
    },
    setBubble: (el) => {
      state.bubble = el;
    },
    setIndicator: (el) => {
      state.indicator = el;
    },
    teardown: () => {
      teardownIframe(state);
      // Remove the wrapper to detach bubble + indicator in one shot.
      state.bubble?.parentElement?.remove();
      state.badge?.remove();
      state.bubble = null;
      state.badge = null;
      state.indicator = null;
    },
  };
}

/** @internal Test-only barrel. */
// eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention -- canonical test-only barrel convention
export const __testing = { buildConfigMessage, resolveEmbedUrl };
